import React, { useState, useEffect, useMemo } from "react"
import { useSelector, useDispatch } from "react-redux"
import { getAccounts } from "../redux/ducks/account";
import { getRoles } from "../redux/ducks/role";
import { getDepartments, getDepartmentByUser, addUsersToDepartment, removeUsersFromDepartment } from "../redux/ducks/department";
import { createUser, getUser, updateUser, updateUserRole, updateUserEmail, activateUser, deactivateUser } from "../redux/slices/users";
import { showAlert } from "../redux/slices/alerts";
import { useHasPermission } from "../utils/permissionUtils"
import DeactivateUserDialog from "./DeactivateUserDialog";
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, InputAdornment, MenuItem, Pagination, Paper, Switch, Tab, TextField, Typography } from "@mui/material"
import { TabContext, TabList, TabPanel } from "@mui/lab"
import { Search } from "@mui/icons-material"
import styled from "@emotion/styled"

const Row = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}))

const Column = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'top',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
}))

const FormField = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const HalfWidthFormField = styled(FormField)(({ theme }) => ({
  width: '50%',
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}))

const AccountBox = styled(Paper)(({ theme }) => ({
  maxHeight: 200,
  overflowX: 'hidden',
  overflowY: 'auto',
  padding: theme.spacing(0),
}))

const AccountBoxItem = styled(FormControlLabel)(({ theme }) => ({
  padding: theme.spacing(0.5, 1),
  width: "100%",
  "&:hover": {
    backgroundColor: "rgba(0, 0, 0, 0.04)"
  },
}))

const NoAccountsRow = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  textAlign: "center",
  color: theme.palette.text.secondary,
}))

const PaginationRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
})

const CreateUserDialog = ({ open, onCancel, onCreate, clientId, isInternal, isEditing, onUpdate, editingUser }) => {
  const userRoleLevel = useSelector(state => state.auth.currentUser?.role?.level ?? Infinity);
  const [roles, setRoles] = useState([]);
  const dispatch = useDispatch();
  const hasPermission = useHasPermission();
  const canSeeInternal = hasPermission('canSeeInternal');
  const canToggleActive = hasPermission('activateUsers') && hasPermission('deactivateUsers');
  const canEditEmail = !isEditing || hasPermission('adminUsers');
  const canManageCalendar = hasPermission('manageCalendar');
  const canListDepartments = hasPermission('listDepartments') && canSeeInternal;
  const canAssignDepartment = hasPermission('addDepartmentUsers');
  const canUnassignDepartment = hasPermission('removeDepartmentUsers');
  const showDepartmentField = Boolean(isInternal) && canListDepartments;
  const currentUserId = useSelector(state => state.auth.userId);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState([])
  const [tabValue, setTabValue] = useState('ContactMethods')
  const [accounts, setAccounts] = useState([]);
  const [accountSearchQuery, setAccountSearchQuery] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false)
  const [savedIdentity, setSavedIdentity] = useState({ email: "", role: "" })
  const [formErrors, setFormErrors] = useState({})
  const [departments, setDepartments] = useState([])
  const [departmentId, setDepartmentId] = useState("")
  const [savedDepartmentId, setSavedDepartmentId] = useState("")
  const accountsPerPage = 50;
  const user = useMemo(() => ({
    client: clientId,
    first_name: "",
    last_name: "",
    title: "",
    email: "",
    preferences: {},
    subscriptions: [],
    contactMethods: {
      phoneNumber: "",
      skype: "",
      whatsapp: "",
    },
    role: "",
    accounts: [],
    active: true,
    isTest: false,
    calendarOptOut: true,
    isInternal: isInternal
  }), [clientId, isInternal]);
  const [userData, setUserData] = useState(user);
  const canChangeDepartment = canAssignDepartment && (canUnassignDepartment || !savedDepartmentId);

  const filteredAccounts = accounts.filter((account) =>
    account.name.toLowerCase().includes(accountSearchQuery.toLowerCase()),
  )

  const totalPages = Math.ceil(filteredAccounts.length / accountsPerPage)
  const paginatedAccounts = filteredAccounts.slice((currentPage - 1) * accountsPerPage, currentPage * accountsPerPage)

  useMemo(() => {
    if (isInternal) {
      dispatch(getAccounts(0, 0, null, false, (data) => {
        setAccounts(data || []);
      }));
    }
  }, [dispatch, isInternal]);

  useEffect(() => {
    if (!open) return;
    if (isEditing) {
      setIsLoading(true)
      dispatch(getUser({userId: editingUser._id, callback: (data) => {
        if (!data) return setIsLoading(false);
        const loaded = { ...data, role: data.role._id, accounts: data.accounts.map((account) => {
          return {
            account: account.account._id,
            role: account.role
          }

        })};
        setUserData(loaded);
        setSelectedAccounts(data.accounts.map((account) => account.account._id));
        setSavedIdentity({ email: data.email || "", role: data.role._id });
        setIsLoading(false)
      }}));
    } else {
      setUserData(user);
      setSelectedAccounts([]);
      setSavedIdentity({ email: "", role: "" });
      setAccountSearchQuery("");
      setCurrentPage(1);
    }
  }, [open, isEditing, editingUser, clientId, user, dispatch]);

  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setFormErrors({});
      if (isInternal) {
        dispatch(getAccounts(0, 0, null, false, (data) => {
          setAccounts(data || []);
        }));
      } else {
        dispatch(
          getAccounts(0, 0, clientId, false, (data) => {
            setAccounts(data || []);
          })
        );
      }

      dispatch(getRoles((data) => {
        setRoles(data);
        setUserData(currentData => ({
          ...currentData,
          role: currentData.role || data.filter((role) => 
            (isInternal ? role.type === "Internal" : role.type === "General") && 
            role.level >= userRoleLevel
          )[0]?._id || ""
        }));
        setIsLoading(false);
      }));
    }
  }, [clientId, dispatch, open, isInternal, userRoleLevel]);

  useEffect(() => {
    if (!open || !showDepartmentField) return;
    dispatch(getDepartments(0, 1000, (data) => setDepartments(data || [])));
  }, [open, showDepartmentField, dispatch]);

  useEffect(() => {
    if (!open || !showDepartmentField) return;
    if (!isEditing || !editingUser?._id) {
      setDepartmentId("");
      setSavedDepartmentId("");
      return;
    }
    dispatch(getDepartmentByUser(editingUser._id, (department) => {
      const assigned = department?._id || "";
      setDepartmentId(assigned);
      setSavedDepartmentId(assigned);
    }));
  }, [open, showDepartmentField, isEditing, editingUser, dispatch]);

  const saveDepartment = (userId, done) => {
    const target = departmentId || "";
    if (!showDepartmentField || !canChangeDepartment || target === (savedDepartmentId || "")) return done(true);

    const addToTarget = () => {
      if (!target) return done(true);
      dispatch(addUsersToDepartment(target, [userId], (result) => done(Boolean(result))));
    };

    if (savedDepartmentId) {
      dispatch(removeUsersFromDepartment(savedDepartmentId, [userId], (result) => {
        if (!result) return done(false);
        addToTarget();
      }));
    } else {
      addToTarget();
    }
  }

  const validateForm = () => {
    if (userData.title?.trim()) return true;
    setFormErrors((prev) => ({ ...prev, title: "Title is required" }));
    return false;
  }

  const handleCreate = () => {
    if (!validateForm()) return;
    setIsLoading(true)
    dispatch(createUser({userData, callback: (data) => {
      if (!data?.user) return setIsLoading(false);
      saveDepartment(data.user._id, (saved) => {
        setIsLoading(false)
        if (!saved) dispatch(showAlert({ severity: 'error', message: `${data.user.first_name} ${data.user.last_name} was created, but the department could not be assigned. Set it in Admin > Departments.` }));
        onCreate(data.user)
        onCancel()
      })
    }}))
  }

  const handleUpdate = () => {
    if (!validateForm()) return;
    setIsLoading(true)
    // email, role and isDeleted each go through their own permission-gated endpoint
    const { active, email, role, isDeleted, ...userDataToSave } = userData;
    const newEmail = (email || "").trim();
    const emailChanged = canEditEmail && newEmail !== "" && newEmail.toLowerCase() !== savedIdentity.email.toLowerCase();
    const roleChanged = role && role !== savedIdentity.role;

    const saveRemainingFields = () => {
      dispatch(updateUser({userId: editingUser._id, userData: userDataToSave, callback: (data) => {
        if (!data?.user) return setIsLoading(false);
        saveDepartment(editingUser._id, (saved) => {
          setIsLoading(false)
          if (!saved) dispatch(showAlert({ severity: 'error', message: "The user was saved, but the department could not be updated. Set it in Admin > Departments." }));
          onUpdate(data.user)
          onCancel()
        })
      }}))
    }

    const saveEmail = () => {
      if (!emailChanged) return saveRemainingFields();
      dispatch(updateUserEmail({userId: editingUser._id, email: newEmail, callback: (data) => {
        if (!data) return setIsLoading(false);
        saveRemainingFields()
      }}))
    }

    if (roleChanged) {
      dispatch(updateUserRole({userId: editingUser._id, roleId: role, callback: (data) => {
        if (!data) return setIsLoading(false);
        saveEmail()
      }}))
    } else {
      saveEmail()
    }
  }

  const handleActiveToggle = (checked) => {
    if (checked) {
      dispatch(activateUser({ userId: editingUser._id, callback: (data) => {
        if (data) setUserData((d) => ({ ...d, active: true }));
      }}));
    } else {
      setDeactivateDialogOpen(true);
    }
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue)
  }

  const handlePageChange = (event, value) => {
    setCurrentPage(value)
  }

  const handleSelectAllAccounts = (event) => {
    if (event.target.checked) {
      setSelectedAccounts(accounts.map((account) => account.id))
      setUserData({ ...userData, accounts: accounts.map((account) => ({ account: account.id, role: roles.find(role => role._id === userData.role)?.name || 'user' })) })
    } else {
      setSelectedAccounts([])
      setUserData({ ...userData, accounts: [] })
    }
  }

  const handleAccountToggle = (accountId) => {
    if (selectedAccounts.includes(accountId)) {
      setSelectedAccounts(selectedAccounts.filter((id) => id !== accountId))
      setUserData({ ...userData, accounts: userData.accounts.filter((account) => account.account !== accountId) })
    } else {
      const selectedRole = roles.find(role => role._id === userData.role)?.name || 'user'
      setSelectedAccounts([...selectedAccounts, accountId])
      setUserData({ ...userData, accounts: [...userData.accounts, { account: accountId, role: selectedRole }] })
    }
  }

  const handleRoleChange = (roleId) => {
    const selectedRole = roles.find(role => role._id === roleId)?.name || 'user'
    setUserData({ 
      ...userData, 
      role: roleId,
      accounts: userData.accounts.map((account) => ({ ...account, role: selectedRole }))
    });
  }

  return (
    <>
    <Dialog open={open} onClose={onCancel} fullWidth maxWidth="sm" disableRestoreFocus>
      <DialogTitle>Create User</DialogTitle>
      <DialogContent dividers>
        <Row>
          <HalfWidthFormField>
            <TextField label="First Name" required fullWidth value={userData?.first_name} onChange={(e) => setUserData({ ...userData, first_name: e.target.value })} />
          </HalfWidthFormField>
          <HalfWidthFormField>
            <TextField label="Last Name" required fullWidth value={userData?.last_name} onChange={(e) => setUserData({ ...userData, last_name: e.target.value })} />
          </HalfWidthFormField>
        </Row>
        <FormField>
          <TextField
            label="Title"
            fullWidth
            required
            value={userData?.title}
            onChange={(e) => {
              setUserData({ ...userData, title: e.target.value })
              if (formErrors.title) {
                setFormErrors((prev) => {
                  const newErrors = { ...prev };
                  delete newErrors.title;
                  return newErrors;
                })
              }
            }}
            error={Boolean(formErrors.title)}
            helperText={formErrors.title || "The person's function at the client, e.g. Billing Manager"}
          />
        </FormField>
        <FormField>
          <TextField
            label="Email"
            type="email"
            fullWidth
            value={userData?.email || ""}
            onChange={(e) => setUserData({ ...userData, email: e.target.value })}
            required
            disabled={!canEditEmail}
            helperText={canEditEmail ? undefined : "Only an administrator can change a user's email address."}
          />
        </FormField>

        <FormField>
          <TabContext value={tabValue}>
            <TabList onChange={handleTabChange}>
              <Tab value="ContactMethods" label="Contact Methods" />
              <Tab value="AssignedAccounts" label="Assigned Accounts" />
            </TabList>

            <TabPanel value="ContactMethods">
              <Column>
                <TextField label="Phone" fullWidth value={userData.contactMethods?.phoneNumber || ""} onChange={(e) => setUserData({ ...userData, contactMethods: { ...userData.contactMethods, phoneNumber: e.target.value } })} />
                <TextField label="Skype" fullWidth value={userData.contactMethods?.skype || ""} onChange={(e) => setUserData({ ...userData, contactMethods: { ...userData.contactMethods, skype: e.target.value } })} />
                <TextField label="WhatsApp" fullWidth value={userData.contactMethods?.whatsapp || ""} onChange={(e) => setUserData({ ...userData, contactMethods: { ...userData.contactMethods, whatsapp: e.target.value } })} />
              </Column>
            </TabPanel>

            <TabPanel value="AssignedAccounts">
              <Column>
                <Row>
                  <Typography variant="body1">Assigned Accounts</Typography>
                  <FormControlLabel control={<Checkbox checked={selectedAccounts.length === accounts.length} onChange={handleSelectAllAccounts} />} label="Select All" />
                </Row>

                <TextField
                  variant="standard"
                  placeholder="Search accounts..."
                  value={accountSearchQuery}
                  onChange={(e) => setAccountSearchQuery(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search />
                      </InputAdornment>
                    ),
                  }}
                />

                <AccountBox>
                  {filteredAccounts.length > 0 ? (
                    paginatedAccounts.map((account) => <AccountBoxItem key={account.id} control={<Checkbox checked={selectedAccounts.includes(account.id)} onChange={() => handleAccountToggle(account.id)} />} label={account.name} />)
                  ) : (
                    <NoAccountsRow>No accounts match your search</NoAccountsRow>
                  )}
                </AccountBox>

                <PaginationRow>
                  <Typography variant="body2" color="text.secondary">
                    {selectedAccounts.length} of {accounts.length} accounts selected
                  </Typography>
                  <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} size="small" />
                </PaginationRow>
              </Column>
            </TabPanel>
          </TabContext>
        </FormField>

        {roles && userData.role !== "" && (
          <FormField>
            <TextField select label="Role" fullWidth value={userData?.role || ''} onChange={(e) => handleRoleChange(e.target.value)} required>
              {roles
                .filter((role) => (isInternal ? role.type === "Internal" : role.type === "General") && role.level >= userRoleLevel)
                .map((role) => (
                  <MenuItem key={role._id} value={role._id}>
                    {role.name}
                  </MenuItem>
                ))}
            </TextField>
          </FormField>
        )}

        {showDepartmentField && (
          <FormField>
            <TextField
              select
              label="Department"
              fullWidth
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              disabled={!canChangeDepartment}
              helperText={canChangeDepartment ? "Optional" : "Only a user with department permissions can change this."}
            >
              <MenuItem value="">None</MenuItem>
              {departments.map((department) => (
                <MenuItem key={department._id} value={department._id}>
                  {department.name}
                </MenuItem>
              ))}
            </TextField>
          </FormField>
        )}

        {!isEditing && canManageCalendar && (
          <Box>
            <FormControlLabel
              control={<Switch checked={!!userData.calendarOptOut} onChange={(e) => setUserData({ ...userData, calendarOptOut: e.target.checked })} />}
              label="Calendar opt-out"
            />
            <Typography variant="caption" color="text.secondary" display="block">
              On by default: no calendar or meeting scheduling for this user. Turn it off for users who need calendar access.
            </Typography>
          </Box>
        )}
        {canSeeInternal && <FormControlLabel control={<Switch checked={userData.isTest} onChange={(e) => setUserData({ ...userData, isTest: e.target.checked })} />} label="Test User" />}
        {isEditing && canToggleActive && editingUser?._id !== currentUserId && <FormControlLabel control={<Switch checked={!!userData.active} onChange={(e) => handleActiveToggle(e.target.checked)} />} label="Active" />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={isLoading}>Cancel</Button>
        {isEditing ? (
          <Button onClick={handleUpdate} variant="contained" disabled={isLoading}>
            {isLoading ? "Updating..." : "Update"}
          </Button>
        ) : (
          <Button onClick={handleCreate} variant="contained" disabled={isLoading}>
            {isLoading ? "Creating..." : "Create"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
    {isEditing && (
      <DeactivateUserDialog
        open={deactivateDialogOpen}
        user={editingUser}
        onClose={() => setDeactivateDialogOpen(false)}
        onConfirm={() => dispatch(deactivateUser({
          userId: editingUser._id,
          callback: (data) => {
            if (data) setUserData((d) => ({ ...d, active: false }));
          },
        }))}
      />
    )}
    </>
  );
}

export default CreateUserDialog;